function resolvePulseApiUrl(): string {
  const configured = (import.meta.env.VITE_API_URL as string | undefined)?.trim().replace(/\/$/, '');
  if (configured) return configured;
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'admin.pulse.igentify.in' || host === 'pulse.igentify.in') {
      return 'https://api.pulse.igentify.in';
    }
  }
  return '';
}

const API_URL = resolvePulseApiUrl();

let idToken: string | null = null;
/** Cognito force-refresh after 401 (wired from auth.ts). */
let refreshIdToken: (() => Promise<string | null>) | null = null;
/** Soft refresh when Bearer is near expiry (wired from auth.ts). */
let ensureFreshIdToken: (() => Promise<string | null>) | null = null;

export function setAdminIdToken(token: string | null) {
  idToken = token;
}

export function setAdminIdTokenRefresher(fn: () => Promise<string | null>) {
  refreshIdToken = fn;
}

export function setAdminIdTokenEnsured(fn: () => Promise<string | null>) {
  ensureFreshIdToken = fn;
}

function isPublicPath(path: string): boolean {
  return path === '/auth/config' || path.startsWith('/auth/');
}

/** True when JWT is missing or expires within `skewSec` seconds. */
function jwtExpiredSoon(token: string | null, skewSec = 120): boolean {
  if (!token) return true;
  try {
    const part = token.split('.')[1];
    if (!part) return true;
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    const payload = JSON.parse(json) as { exp?: number };
    if (!payload.exp) return true;
    return Date.now() / 1000 >= payload.exp - skewSec;
  } catch {
    return true;
  }
}

function redirectToLogin() {
  if (typeof window === 'undefined') return;
  const path = window.location.pathname || '';
  if (
    path.startsWith('/login')
    || path.startsWith('/invite')
    || path.startsWith('/reset-password')
  ) {
    return;
  }
  window.location.assign(`/login?next=${encodeURIComponent(path + window.location.search)}`);
}

async function req<T>(path: string, init: RequestInit = {}, retried = false): Promise<T> {
  const headers = new Headers(init.headers || {});
  if (!headers.has('content-type') && init.body) headers.set('content-type', 'application/json');

  if (!isPublicPath(path) && !retried) {
    if (jwtExpiredSoon(idToken) && ensureFreshIdToken) {
      await ensureFreshIdToken();
    } else if (jwtExpiredSoon(idToken) && refreshIdToken) {
      await refreshIdToken();
    }
  }

  if (idToken) headers.set('authorization', `Bearer ${idToken}`);
  const res = await fetch(`${API_URL}${path}`, { ...init, headers });
  const text = await res.text();
  let data: unknown = {};
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = { error: text };
  }

  if (res.status === 401 && !retried && !isPublicPath(path) && refreshIdToken) {
    const next = await refreshIdToken();
    if (next) return req<T>(path, init, true);
    setAdminIdToken(null);
    redirectToLogin();
    throw new Error('Session expired — sign in again');
  }

  if (!res.ok) {
    if (res.status === 401) {
      setAdminIdToken(null);
      redirectToLogin();
    }
    // Control-api RBAC errors: { error: { code, message, correlationId } }
    // Older/simple errors may use top-level message/error string.
    const body = data as {
      error?: string | { code?: string; message?: string; correlationId?: string };
      message?: string;
      code?: string;
      correlationId?: string;
    };
    const nested = typeof body.error === 'object' && body.error ? body.error : null;
    const rawMessage =
      body.message ||
      nested?.message ||
      (typeof body.error === 'string' ? body.error : '') ||
      res.statusText ||
      `HTTP ${res.status}`;
    const message = typeof rawMessage === 'string' ? rawMessage : 'Request failed';
    throw new ApiError(message, {
      status: res.status,
      code: nested?.code || body.code,
      correlationId: nested?.correlationId || body.correlationId,
    });
  }
  return data as T;
}

export class ApiError extends Error {
  status: number;
  code?: string;
  correlationId?: string;

  constructor(
    message: string,
    opts: { status: number; code?: string; correlationId?: string },
  ) {
    super(
      opts.correlationId
        ? `${message} (ref: ${opts.correlationId})`
        : message,
    );
    this.name = 'ApiError';
    this.status = opts.status;
    this.code = opts.code;
    this.correlationId = opts.correlationId;
  }
}

export type SessionCostLine = {
  id: string;
  label: string;
  usd: number;
  detail?: string | null;
  qty?: Record<string, unknown> | null;
};

export type SessionCostBreakdown = {
  method?: string;
  currency?: string;
  ratesNote?: string;
  lines?: SessionCostLine[];
  totalUsd?: number;
};

export type SessionSummary = {
  sessionId: string;
  transport?: string;
  status?: string;
  displayStatus?: 'Live' | 'Completed' | 'Review' | 'Failed';
  createdAt?: string;
  updatedAt?: string;
  lastReply?: string;
  lastUserText?: string;
  topic?: string | null;
  agentId?: string | null;
  agentName?: string | null;
  /** Published version number stamped at session start (audit). */
  agentVersion?: number | null;
  agentPublished?: boolean | null;
  /** Cascade vs Direct vs LiveAvatar, stamped at session start when known. */
  agentMode?: 'cascade' | 'direct' | 'liveavatar' | null;
  durationSec?: number | null;
  perceptionMood?: string | null;
  /** Fused emotion engagement 0–100 from emotion/v1/session.json (not qualityScore). */
  emotionEngagement?: number | null;
  emotionFusionStatus?: string | null;
  emotionSession?: {
    sessionMood?: string | null;
    engagement?: number | null;
    dominantEmotions?: string[];
    metrics?: { engagement?: number; beatCount?: number };
    rationale?: string | null;
  } | null;
  /** Topic diamonds from Interaction Profile (separate from emotion keyMoments). */
  topicMoments?: Array<{ at: number; label: string; color?: string }> | null;
  qualityScore?: number | null;
  costUsd?: number | null;
  costBreakdown?: SessionCostBreakdown | null;
  /** Reference $/min for this session's feature set (avatar, LP, Oruk, egress…). */
  oneMinuteCost?: SessionCostBreakdown | null;
  statusReason?: string | null;
  endReason?: string | null;
  abandonReason?: string | null;
  waveformStatus?: string | null;
  userName?: string | null;
  userEmail?: string | null;
  /** Platform owner stamped at session start (for role-scope filters). */
  ownerUserId?: string | null;
  ownerEmail?: string | null;
  ownerRoleId?: string | null;
  /** e.g. autotest — used with sessions.view_autotest. */
  source?: string | null;
  groq429Count?: number | null;
  providerDegraded?: boolean | null;
  degradedReason?: string | null;
  degradedReasonCode?: string | null;
  degradedAt?: string | null;
  lastProviderError?: string | null;
  lastProviderErrorAt?: string | null;
  clientAudioIssue?: "audio_blocked" | "audio_play_failed" | string | null;
  clientAudioIssueAt?: string | null;
  clientAudioIssueMessage?: string | null;
  clientAudioResumedAt?: string | null;
  videoEgressStatus?: string | null;
  videoPerceptionStatus?: string | null;
  perceptionFusion?: PerceptionFusion | null;
  /** Compact SER valence series for the Perception column Voice sparkline (≥2 points). */
  voiceSpark?: number[] | null;
  agentAvatarUrl?: string | null;
};

export type PerceptionChannelState = 'ready' | 'pending' | 'failed' | 'none';

/** Read-time fusion of language / voice / video signals (control-api). */
export type PerceptionFusion = {
  status: 'live' | 'needs_review' | 'pending' | 'partial' | 'ready' | 'none';
  label: string;
  tone: 'green' | 'blue' | 'orange' | 'red' | 'gray';
  channels: {
    language: PerceptionChannelState;
    voice: PerceptionChannelState;
    video: PerceptionChannelState;
  };
  reasons: string[];
};

export type VideoStageScores = Partial<
  Record<string, { facialTension?: number; gazeFocus?: number; postureShift?: number }>
>;

export type VideoPerceptionSummary = {
  schemaVersion?: string;
  sessionId?: string;
  status?: string;
  modelVersion?: string;
  analyzerVersion?: string;
  provider?: string;
  summary?: string;
  signals?: string;
  appearance?: string;
  environment?: string;
  movement?: string;
  notes?: {
    signals?: string[];
    appearance?: string[];
    environment?: string[];
    movement?: string[];
  };
  chips?: string[];
  observations?: unknown;
  quality?: unknown;
  stageScores?: VideoStageScores;
  schemaRemapped?: boolean;
  observationsKey?: string;
  updatedAt?: string;
};

export type SessionDetail = SessionSummary & {
  /** Which remote audio path the client preferred (auto | liveavatar | tts). */
  avatarAudioPath?: 'auto' | 'liveavatar' | 'tts' | string | null;
  videoAvatarEnabled?: boolean;
  liveAvatarSandbox?: boolean;
  liveAvatarId?: string | null;
  /** FULL client MediaRecorder archives (vs Track Egress). */
  archiveSource?: 'client' | 'egress' | string | null;
  /** Stamped at session start when agent had client archive on (LiveAvatar FULL). */
  clientSessionArchiveEnabled?: boolean;
  latencies?: unknown[];
  recordingKeys?: Record<string, string>;
  timelineAudioUrl?: string;
  audioUrl?: string;
  userAudioUrl?: string;
  botAudioUrl?: string;
  userVideoUrl?: string;
  botVideoUrl?: string;
  /** Seconds to subtract from session audio clock when scrubbing user camera (egress start skew). */
  videoOffsetSec?: number | null;
  trackOffsetsSec?: {
    userVideo?: number;
    userAudio?: number;
    botVideo?: number;
    botAudio?: number;
  } | null;
  sessionWallStartUnix?: number | null;
  clockAudioSource?: "userAudio" | "botAudio" | string | null;
  archiveUserAudio?: boolean;
  archiveBotAudio?: boolean;
  metaUrl?: string;
  transcriptUrl?: string;
  eventsUrl?: string;
  livePerceptionEventsUrl?: string;
  perceptionReportUrl?: string;
  perceptionReportStatus?: string | null;
  livePerceptionStatus?: string | null;
  turns?: Array<{ role?: string; text?: string; audioUrl?: string; startMs?: number; endMs?: number; durationMs?: number }>;
  transcripts?: Array<{
    role?: string;
    text?: string;
    startMs?: number;
    kind?: string;
    url?: string;
    frameUrl?: string;
    unrecognized?: boolean;
  }>;
  perceptionAnalysis?: unknown;
  videoPerception?: VideoPerceptionSummary | null;
  videoKey?: string | null;
  videoPerceptionKey?: string | null;
  videoPerceptionModel?: string | null;
  videoPerceptionUpdatedAt?: string | null;
  videoPerceptionError?: string | null;
  videoStageScores?: VideoStageScores | null;
  needsReview?: boolean;
  voiceCoverage?: boolean;
  affectSource?: string | null;
  videoEgressStatus?: string | null;
  videoPerceptionStatus?: string | null;
  /** Camera / bot video archives permanently deleted from this session. */
  videoRemoved?: boolean;
  videoRemovedAt?: string | null;
  videoUrl?: string | null;
  degradedReason?: string | null;
  degradedReasonCode?: string | null;
  degradedAt?: string | null;
  lastProviderError?: string | null;
  lastProviderErrorAt?: string | null;
  segmentAudioUrls?: Array<{ role?: string; text?: string; audioUrl?: string; startMs?: number }>;
  waveformPeaks?: number[] | null;
  /** Bot/avatar archive peaks (dual-lane waveform); null when no botAudio. */
  waveformPeaksBot?: number[] | null;
  keyMoments?: Array<{ at: number; label: string; color?: string }> | null;
};

export type AcousticEventMode = "observe" | "context" | "respond_if_idle" | "respond";

export type AcousticEventPolicy = {
  label: string;
  mode: AcousticEventMode;
  instruction?: string;
  ttsEmotion?: string | null;
  cooldownSec?: number;
  /** Prepend to TTS when speaking (e.g. [laughter] for Cartesia). */
  insertNonverbal?: string | null;
};

/** Per-agent live knowledge source binding (pluggable by type). */
export type KnowledgeConnectorBinding = {
  id: string;
  type: 'http_api' | string;
  enabled?: boolean;
  credentialSlotId?: string | null;
  config?: {
    baseUrl?: string;
    timeoutMs?: number;
    tools?: Array<{
      name: string;
      description: string;
      method?: 'GET' | 'POST' | string;
      path?: string;
      parameters?: {
        type?: string;
        properties?: Record<string, unknown>;
        required?: string[];
      };
    }>;
  };
};

export type Agent = {
  agentId: string;
  name: string;
  /** Cascade = STT→LLM→TTS. Direct = GPT-Live mouth + Pulse brain. LiveAvatar = LA FULL. */
  agentMode?: 'cascade' | 'direct' | 'liveavatar';
  /** GPT-Live model id (Direct only). */
  directModel?: string | null;
  /** GPT-Live voice name (Direct only). */
  directVoice?: string | null;
  /** ElevenLabs voice id selected for LiveAvatar FULL (imported into LA on save). */
  elevenLabsVoiceId?: string | null;
  /** LiveAvatar voice id after EL import (Custom TTS). */
  liveAvatarVoiceId?: string | null;
  /** LiveAvatar Context id synced from greeting / liveAvatarPrompt (+ links). */
  liveAvatarContextId?: string | null;
  /**
   * LiveAvatar FULL Context `prompt` (system+knowledge in one field).
   * Hard cap 65535 chars (LiveAvatar store limit). Cascade/Direct use systemPrompt + knowledgeBase.
   */
  liveAvatarPrompt?: string;
  /** LiveAvatar Context reference URLs (FULL only). */
  liveAvatarLinks?: Array<{ url: string; faq: string; id?: string }>;
  /** Short customer-facing role label (e.g. Sales Guide). */
  role?: string;
  greetingText?: string;
  systemPrompt?: string;
  knowledgeBase?: string;
  /**
   * Live knowledge connector bindings (Direct brain tools).
   * Markdown knowledgeBase stays curated prose; connectors supply lookup tools.
   */
  knowledgeConnectors?: KnowledgeConnectorBinding[];
  guardsPrompt?: string;
  /** When true, AUX classifier can interrupt the reply on policy violation. */
  guardrailEnabled?: boolean;
  /** fixed = always speak guardrailRedirectText; adaptive = short generated refuse. */
  guardrailRedirectStyle?: 'fixed' | 'adaptive';
  /** Spoken when a guard fires (Fixed line). */
  guardrailRedirectText?: string;
  /** Tone guidance for Adaptive redirect generation. */
  guardrailRedirectGuidance?: string;
  semanticPrompt?: string;
  briefComplete?: string;
  incompleteTails?: string;
  hangupPrompt?: string;
  affirmPrompt?: string;
  affectPrompt?: string;
  /** Video perception: what to notice in the webcam recording. */
  videoPerceptionFocus?: string;
  /** Video perception: extra observation rules. */
  videoPerceptionRules?: string;
  /** Video perception: chip vocabulary (one per line). */
  videoPerceptionChips?: string;
  hangupConfirmText?: string;
  hangupGoodbyeText?: string;
  /** Soft semantic-endpointing nudge (e.g. "Yes?"). */
  semanticNudgeText?: string;
  /** Idle presence nudge after silence (e.g. "Are you still here?"). */
  idlePrompt?: string;
  /** Spoken line before idle terminate. */
  idleGoodbyeText?: string;
  voiceId?: string | null;
  /** Denormalized display name for the selected Cartesia voice. */
  voiceName?: string | null;
  /** Cartesia generation_config.speed [0.6–1.5]. */
  ttsSpeed?: number | null;
  /** LiveAvatar ElevenLabs speed [0.8–1.2] on FULL session token; null = map from ttsSpeed. */
  liveAvatarVoiceSpeed?: number | null;
  liveAvatarVoiceStability?: number | null;
  liveAvatarVoiceSimilarityBoost?: number | null;
  liveAvatarVoiceStyle?: number | null;
  liveAvatarVoiceModel?: string | null;
  liveAvatarVoiceSpeakerBoost?: boolean;
  liveAvatarVoiceTextNormalization?: boolean;
  liveAvatarVideoQuality?: string | null;
  liveAvatarVideoEncoding?: string | null;
  /** Cartesia generation_config.volume [0.5–2.0]. */
  ttsVolume?: number | null;
  /** Cartesia generation_config.emotion, or null for auto. */
  ttsEmotion?: string | null;
  /** Hard session length in minutes (clamped 3–30 on save). */
  sessionMaxMinutes?: number;
  /** Minutes before hard end to warn (clamped 1 .. sessionMaxMinutes-1). */
  sessionWarnMinutes?: number;
  idleEnabled?: boolean;
  /** Silence seconds before each idle nudge (clamped 3–60; default 8). */
  idleSeconds?: number;
  /** Unanswered idle nudges before goodbye (clamped 1–3; default 1). */
  idleMaxNudges?: number;
  published?: boolean;
  version?: number;
  publishedAt?: string | null;
  /** Soft-deleted from active use; cannot start sessions. */
  archived?: boolean;
  archivedAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
  avatarUrl?: string | null;
  /** S3 object key under artifacts (agents/{id}/cover-….jpg). */
  avatarKey?: string | null;
  /** When true and liveAvatarId is ready, worker starts LiveAvatar LITE (off-path video). */
  videoAvatarEnabled?: boolean;
  /**
   * LiveAvatar is_sandbox on session token. Free short sessions; only the
   * LiveAvatar sandbox avatar id works. Preset/Custom need this off (billed).
   */
  liveAvatarSandbox?: boolean;
  /** LiveAvatar avatar UUID bound in Admin (created in LiveAvatar dashboard). */
  liveAvatarId?: string | null;
  /** none | provisioning | ready | failed */
  liveAvatarStatus?: 'none' | 'provisioning' | 'ready' | 'failed' | string | null;
  /** Last bind / provision error (short). */
  liveAvatarError?: string | null;
  /**
   * LiveAvatar FULL only: browser MediaRecorder → same Admin archive slots
   * (recordings/livekit/…). Off by default. Ignored for Cascade/Direct.
   */
  clientSessionArchiveEnabled?: boolean;
  /**
   * When false, Conversation complete page hides on-screen transcript and PDF download.
   * Default on when unset.
   */
  completePageTranscriptEnabled?: boolean;
  /**
   * Direct only: after Live sources tools succeed, publish pulse.insight_card on the
   * data channel and keep spoken answers qualitative (numbers on the card). Off by default.
   */
  liveInsightCardsEnabled?: boolean;
  /** In-call Live Perception silent participant (off by default). */
  livePerceptionEnabled?: boolean;
  livePerceptionLanguageModel?: string | null;
  livePerceptionVoiceMode?: string | null;
  livePerceptionVideoModel?: string | null;
  livePerceptionDebugFramesInTranscript?: boolean;
  /** CED-tiny AudioSet tags on the mic ring (off by default). */
  livePerceptionAcousticEventsEnabled?: boolean;
  /** Per-label detection + bot policy (Log only / Context / Respond*). */
  livePerceptionAcousticEventsPolicies?: AcousticEventPolicy[] | null;
  /** Derived AudioSet labels for LP CED filter (from policies). */
  livePerceptionAcousticEventsWhitelist?: string[] | null;
  /** Min CED score to emit (default 0.55). */
  livePerceptionAcousticEventsThreshold?: number | null;
  psychologistReviewEnabled?: boolean;
  psychologistReviewModel?: string | null;
  /** Include acoustic events in Interaction Profile (live jsonl or offline CED on userAudio). */
  interactionProfileAcousticsEnabled?: boolean;
  /** Include offline SER / voice timeline in Interaction Profile scores. */
  interactionProfileOfflineVoiceEnabled?: boolean;
};

export type LiveAvatarRow = {
  id: string;
  name: string;
  type?: string | null;
  status?: string | null;
  previewUrl?: string | null;
  isExpired?: boolean;
  errorMessage?: string | null;
  /** preset = GET /v1/avatars/public; custom = GET /v1/avatars */
  source?: 'preset' | 'custom' | null;
};

export type AgentVersion = Agent & {
  publishedBy?: string | null;
  sk?: string | null;
};

export type Settings = {
  defaultAgentId?: string | null;
  voiceId?: string | null;
  voiceName?: string | null;
  ttsSpeed?: number | null;
  ttsVolume?: number | null;
  ttsEmotion?: string | null;
  idleEnabled?: boolean;
  semanticVerifyEnabled?: boolean;
  credentialSlots?: CredentialSlot[];
  llmRouting?: LlmRouting;
  offlineAnalysis?: OfflineAnalysis;
  modelCatalog?: {
    realtime?: Record<string, string[]>;
    offline?: Record<string, string[]>;
    vision?: Record<string, string[]>;
  };
};

export type CartesiaVoice = {
  id: string;
  name: string;
  tagline?: string;
  description?: string;
  gender?: string | null;
  language?: string | null;
  locale?: string | null;
  accent?: string | null;
  country?: string | null;
  hasPreview?: boolean;
  isPro?: boolean;
};

export type CredentialSlot = {
  id: string;
  label: string;
  provider: string;
  roles: string[];
  preview?: string;
  status?: string;
  updatedAt?: string | null;
  secret?: string;
};

export type LlmRoutingEntry = {
  priority: number;
  slotId: string;
  modelId: string;
};

export type LlmRouting = {
  autoPrioritize?: boolean;
  chain: LlmRoutingEntry[];
  lastProbeAt?: string | null;
  lastProbeByEntry?: Record<
    string,
    { p50Ms: number; p90Ms: number; verdict: string; measuredAt: string }
  >;
};

export type OfflineAnalysis = {
  topic: { slotId: string; modelId: string };
  journey: { slotId: string; modelId: string };
  video?: { slotId: string; modelId: string };
};

export type ProbeResult = {
  slotId: string;
  modelId: string;
  provider?: string;
  ok: boolean;
  p50Ms?: number;
  p90Ms?: number;
  verdict?: string;
  sample?: string;
  error?: string;
  measuredAt?: string;
};

export type CheckupResult = {
  resultId: string;
  protocolId: string;
  status: 'pass' | 'fail';
  sessionId?: string | null;
  notes: string;
  createdAt: string;
  createdBy?: string | null;
  createdByEmail?: string | null;
};

export type CheckupProtocolMeta = {
  protocolId: string;
  latestStatus: 'pass' | 'fail' | null;
  lastResultAt?: string | null;
  lastResultId?: string | null;
  lastSessionId?: string | null;
  lastNotes?: string | null;
  lastCreatedBy?: string | null;
  passCount: number;
  failCount: number;
  totalCount: number;
};

export type CheckupDayStat = {
  date: string;
  pass: number;
  fail: number;
  total: number;
};

export type CheckupStats = {
  totalResults: number;
  passCount: number;
  failCount: number;
  passRate: number | null;
  failingProtocols: number;
  passingProtocols: number;
  neverRun: number;
  last14Days: CheckupDayStat[];
};

export type CheckupsResponse = {
  byProtocol: Record<string, CheckupProtocolMeta>;
  results: CheckupResult[];
  stats: CheckupStats;
};

export type CheckupDetailResponse = {
  protocolId: string;
  meta: CheckupProtocolMeta | null;
  results: CheckupResult[];
};

export type PlatformUserStatus = 'ACTIVE' | 'INVITED' | 'SUSPENDED' | 'REMOVED';

export type PlatformUserSecurity =
  | { kind: 'mfa'; label: string; mfaEnabled?: boolean }
  | { kind: 'invitation'; label: string; sentAt?: string | null };

export type PlatformUser = {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  status: PlatformUserStatus | string;
  roleId: string;
  roleName: string;
  security?: PlatformUserSecurity;
  addedBy?: string;
  lastActive?: string | null;
  createdAt?: string;
  version?: number;
  canResend?: boolean;
};

export type UsersListResponse = {
  users: PlatformUser[];
  total: number;
  limit: number;
  offset: number;
};

export type PermissionDef = {
  key: string;
  group?: string;
  label: string;
  description: string;
};

export type PermissionGroup = {
  name: string;
  permissions: PermissionDef[];
};

export type PlatformRole = {
  roleId: string;
  name: string;
  description?: string;
  type: 'SYSTEM' | 'CUSTOM' | string;
  userCount?: number;
  permissionScope?: string[];
  permissions?: string[];
  /** Empty = no agents. */
  allowedAgentIds?: string[];
  version?: number;
  updatedAt?: string;
  systemImmutable?: boolean;
  permissionCatalog?: PermissionGroup[];
};

export type RolesListResponse = {
  roles: PlatformRole[];
  total: number;
  limit: number;
  offset: number;
  permissionCatalog?: PermissionGroup[];
};

export type AuditEventListItem = {
  eventId: string;
  createdAt: string;
  action: string;
  title: string;
  category: string;
  actorId?: string | null;
  actorEmail?: string | null;
  actorDisplay?: string | null;
  actorRole?: string | null;
  resourceType?: string | null;
  resourceId?: string | null;
  resourceLabel?: string | null;
  source?: string | null;
  sourceIp?: string | null;
  outcome?: string | null;
  risk?: string | null;
  environment?: string | null;
  sessionId?: string | null;
  agentId?: string | null;
};

export type AuditEventDetail = AuditEventListItem & {
  userAgent?: string | null;
  correlationId?: string | null;
  before?: Record<string, unknown> | null;
  after?: Record<string, unknown> | null;
  relatedEventIds?: string[];
  permission?: string | null;
  publishChecks?: Record<string, unknown> | null;
  targetId?: string | null;
  payload?: Record<string, unknown>;
};

export type AuditListResponse = {
  events: AuditEventListItem[];
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
  nextOffset: number | null;
  metrics: {
    events: number;
    privileged: number;
    highRisk: number;
    failedAccess: number;
  };
  from: string;
  to: string;
};

export type AuditExportPack = {
  exportedAt: string;
  environment: string;
  from: string;
  to: string;
  total: number;
  metrics: AuditListResponse['metrics'];
  events: AuditEventListItem[];
};

export type AdminMe = {
  userId: string;
  email: string;
  firstName?: string;
  lastName?: string;
  roleId?: string;
  roleName?: string;
  status?: string;
  permissions: string[];
  allowedAgentIds?: string[];
};

export type UserSettings = {
  hideOwnSessionsFromOthers: boolean;
};

export type Overview = {
  concurrentSessions: number;
  queueDepth: number;
  workerCount: number;
  desiredWorkers?: number;
  maxConcurrent?: number;
  range?: string;
  generatedAt?: string | null;
  agentAvailabilityPct?: number | null;
  e2eP50Ms?: number | null;
  e2eP95Ms?: number | null;
  e2eSamples?: number;
  spendTotalUsd?: number | null;
  spendTodayUsd?: number | null;
  spendSource?: string;
  spendBreakdown?: {
    awsUsd?: number | null;
    providersUsd?: number;
    deepgramUsd?: number;
    groqUsd?: number;
    cartesiaUsd?: number;
    livekitUsd?: number;
    sessionsToday?: number;
    sessionsInRange?: number;
    estimatedMinutes?: number;
  };
  /** Fully-loaded 1-minute list-price estimate (avatar + LP + Oruk + offline perception). */
  oneMinuteCost?: SessionCostBreakdown | null;
  pipeline: Array<{
    name: string;
    status: string;
    avgMs?: number | null;
    p95Ms?: number | null;
    samples?: number;
    note?: string;
    degradedReason?: string;
  }>;
  providerHealth?: {
    Groq?: {
      status?: string;
      rateLimitEvents?: number;
      sessionsAffected?: number;
    };
  } | null;
  charts?: Record<string, {
    series?: number[];
    pathA?: string;
    pathB?: string;
    max?: number;
    maxLabel?: string;
    labels?: string[];
    legendA?: string;
    legendB?: string;
  }> | null;
  sessionsInRange?: number;
  source: 'live' | 'mock' | 'rollup' | 'live-fallback';
};

function num(v: unknown, fallback = 0): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

/** Accept flat or nested `{ capacity, e2e }` overview payloads. */
export function normalizeOverview(raw: unknown): Overview {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const cap = (r.capacity && typeof r.capacity === 'object' ? r.capacity : {}) as Record<string, unknown>;
  const e2e = (r.e2e && typeof r.e2e === 'object' ? r.e2e : {}) as Record<string, unknown>;
  const breakdown = (r.spendBreakdown && typeof r.spendBreakdown === 'object'
    ? r.spendBreakdown
    : undefined) as Overview['spendBreakdown'];
  const oneMinuteRaw = (r.oneMinuteCost && typeof r.oneMinuteCost === 'object'
    ? r.oneMinuteCost
    : null) as SessionCostBreakdown | null;
  const oneMinuteCost = oneMinuteRaw
    ? {
        ...oneMinuteRaw,
        lines: Array.isArray(oneMinuteRaw.lines) ? oneMinuteRaw.lines : [],
        totalUsd: oneMinuteRaw.totalUsd != null ? num(oneMinuteRaw.totalUsd) : undefined,
      }
    : null;
  const pipeline = Array.isArray(r.pipeline)
    ? (r.pipeline as Overview['pipeline'])
    : [
        { name: 'LiveKit', status: 'healthy' },
        { name: 'Deepgram', status: 'healthy' },
        { name: 'Groq', status: 'healthy' },
        { name: 'Cartesia', status: 'healthy' },
        { name: 'HeyGen', status: 'healthy' },
        { name: 'Storage', status: 'healthy' },
      ];
  return {
    concurrentSessions: num(r.concurrentSessions ?? cap.concurrentSessions),
    queueDepth: num(r.queueDepth ?? cap.queueDepth),
    workerCount: num(r.workerCount ?? cap.runningWorkers),
    desiredWorkers: num(r.desiredWorkers ?? cap.desiredWorkers, 0),
    maxConcurrent: num(r.maxConcurrent ?? cap.maxConcurrent, 50),
    range: typeof r.range === 'string' ? r.range : undefined,
    generatedAt: typeof r.generatedAt === 'string' ? r.generatedAt : null,
    agentAvailabilityPct: r.agentAvailabilityPct != null ? num(r.agentAvailabilityPct) : null,
    e2eP50Ms: r.e2eP50Ms != null ? num(r.e2eP50Ms) : e2e.p50 != null ? num(e2e.p50) : null,
    e2eP95Ms: r.e2eP95Ms != null ? num(r.e2eP95Ms) : e2e.p95 != null ? num(e2e.p95) : null,
    e2eSamples: num(r.e2eSamples ?? e2e.samples),
    spendTotalUsd: r.spendTotalUsd != null ? num(r.spendTotalUsd) : r.spendTodayUsd != null ? num(r.spendTodayUsd) : null,
    spendTodayUsd: r.spendTodayUsd != null ? num(r.spendTodayUsd) : r.spendTotalUsd != null ? num(r.spendTotalUsd) : null,
    spendSource: typeof r.spendSource === 'string' ? r.spendSource : undefined,
    spendBreakdown: breakdown,
    oneMinuteCost,
    pipeline,
    providerHealth: (r.providerHealth && typeof r.providerHealth === 'object'
      ? r.providerHealth
      : null) as Overview['providerHealth'],
    charts: (r.charts && typeof r.charts === 'object' ? r.charts : null) as Overview['charts'],
    sessionsInRange: r.sessionsInRange != null ? num(r.sessionsInRange) : undefined,
    source:
      r.source === 'mock' ? 'mock'
        : r.source === 'rollup' ? 'rollup'
          : r.source === 'live-fallback' ? 'live-fallback'
            : 'live',
  };
}

export type PerceptionCloud = {
  name: string;
  x: number;
  y: number;
  rx: number;
  ry: number;
  color: string;
  count?: number;
};

export type PerceptionPoint = {
  valence: number;
  arousal: number;
  source?: string;
  label?: string;
  color: string;
  sessionId?: string;
  atMs?: number | null;
  key?: string;
  x?: number;
  y?: number;
};

export type Perception = {
  range?: string;
  generatedAt?: string | null;
  source?: 'live' | 'mock' | 'rollup' | 'live-fallback';
  sessionsInRange?: number;
  summary: {
    multimodalCoveragePct: number;
    multimodalCoverageDelta: number | null;
    highConfidenceSignals: number;
    highConfidenceDelta: number | null;
    concernRecoveryPct: number;
    concernRecoveryDelta: number | null;
    needsReview: number;
    needsReviewDelta: number | null;
  };
  landscape: { clouds: PerceptionCloud[]; points: PerceptionPoint[] };
  heatmap: { stages: string[]; rows: string[]; colors: string[][]; values?: (number | null)[][] | null };
  behaviorPaths: {
    triggers: string[][];
    states: string[][];
    outcomes: string[][];
    flows: {
      trigger: string;
      state: string;
      outcome: string;
      triggerIdx: number;
      stateIdx: number;
      outcomeIdx: number;
      count: number;
      pct: number;
      weight: number;
      color: string;
    }[];
    noteTitle: string;
    noteBody: string;
  };
  concernDrivers: { label: string; value: number; color: string }[];
  concernDeltaPct: number | null;
  insight: string;
  insightBanner: string;
  calibration: {
    precision: number;
    recall: number;
    concernThreshold: number;
    confidenceFloor: number;
    requireTwoModalities: boolean;
    policy: string;
    reviewQueueCount: number;
    curve: { x: number; y: number }[];
    metricNote?: string;
    dualModalitySessions?: number;
    scoreCount?: number;
  };
  /** Sessions in range with each channel ready (videoExpected = sessions that recorded video). */
  channels: { sessions: number; language: number; voice: number; video: number; videoExpected: number } | null;
  fusion: Partial<Record<PerceptionFusion['status'], number>> | null;
  agents: string[];
};

const MOCK_CLOUDS: PerceptionCloud[] = [
  { name: 'Frustrated', x: 110, y: 66, rx: 55, ry: 31, color: '#ff6b65' },
  { name: 'Concerned', x: 156, y: 151, rx: 65, ry: 31, color: '#f59e0b' },
  { name: 'Confused', x: 327, y: 174, rx: 63, ry: 23, color: '#8b4cf5' },
  { name: 'Neutral', x: 403, y: 115, rx: 76, ry: 45, color: '#1c7dff' },
  { name: 'Engaged', x: 466, y: 49, rx: 71, ry: 32, color: '#8646f5' },
  { name: 'Positive', x: 646, y: 80, rx: 74, ry: 40, color: '#11bf82' },
];

export function normalizePerception(raw: unknown): Perception {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const summary = (r.summary && typeof r.summary === 'object' ? r.summary : {}) as Record<string, unknown>;
  const landscape = (r.landscape && typeof r.landscape === 'object' ? r.landscape : {}) as Record<string, unknown>;
  const heatmap = (r.heatmap && typeof r.heatmap === 'object' ? r.heatmap : {}) as Record<string, unknown>;
  const behavior = (r.behaviorPaths && typeof r.behaviorPaths === 'object' ? r.behaviorPaths : {}) as Record<string, unknown>;
  const calib = (r.calibration && typeof r.calibration === 'object' ? r.calibration : {}) as Record<string, unknown>;
  const channels = (r.channels && typeof r.channels === 'object' ? r.channels : null) as Record<string, unknown> | null;
  const fusion = (r.fusion && typeof r.fusion === 'object' ? r.fusion : null) as Record<string, unknown> | null;
  const clouds = Array.isArray(landscape.clouds) && landscape.clouds.length
    ? (landscape.clouds as PerceptionCloud[])
    : MOCK_CLOUDS;
  const pointsRaw = Array.isArray(landscape.points) ? (landscape.points as PerceptionPoint[]) : [];
  const points = pointsRaw.map((p, i) => ({
    ...p,
    color: p.color || '#1c7dff',
    key: p.key || `${p.sessionId || 'p'}-${i}`,
    x: p.x ?? Number(p.valence) * 720,
    y: p.y ?? (1 - Number(p.arousal)) * 210,
  }));
  const defaultHeat = [
    ['#d8e8fb', '#d8d3fa', '#bed7f8', '#d7e6fb', '#c2d9f7'],
    ['#d8e7f7', '#bbd4f7', '#d8e8fa', '#c7ddf9', '#d7d1f4'],
    ['#ffd2d2', '#ff9c9c', '#ff6969', '#ffb0ae', '#ffd5d2'],
    ['#b9eedc', '#7ee0be', '#9be7cd', '#a9e8d1', '#bae9d8'],
    ['#c3d8f4', '#d8e6f8', '#c2d7f4', '#a9c9f3', '#c3d8f5'],
    ['#9fe7d1', '#9dbcf2', '#ffd2d0', '#ffd5b5', '#caefd9'],
  ];
  return {
    range: typeof r.range === 'string' ? r.range : undefined,
    generatedAt: typeof r.generatedAt === 'string' ? r.generatedAt : null,
    source:
      r.source === 'mock' ? 'mock'
        : r.source === 'rollup' ? 'rollup'
          : r.source === 'live-fallback' ? 'live-fallback'
            : 'live',
    sessionsInRange: r.sessionsInRange != null ? num(r.sessionsInRange) : undefined,
    summary: {
      multimodalCoveragePct: num(summary.multimodalCoveragePct),
      multimodalCoverageDelta: summary.multimodalCoverageDelta != null ? num(summary.multimodalCoverageDelta) : null,
      highConfidenceSignals: num(summary.highConfidenceSignals),
      highConfidenceDelta: summary.highConfidenceDelta != null ? num(summary.highConfidenceDelta) : null,
      concernRecoveryPct: num(summary.concernRecoveryPct),
      concernRecoveryDelta: summary.concernRecoveryDelta != null ? num(summary.concernRecoveryDelta) : null,
      needsReview: num(summary.needsReview),
      needsReviewDelta: summary.needsReviewDelta != null ? num(summary.needsReviewDelta) : null,
    },
    landscape: { clouds, points },
    heatmap: {
      stages: Array.isArray(heatmap.stages) ? (heatmap.stages as string[]) : ['greeting', 'needs', 'explanation', 'decision', 'closing'],
      rows: Array.isArray(heatmap.rows) ? (heatmap.rows as string[]) : ['voiceEnergy', 'speechRate', 'facialTension', 'gazeFocus', 'postureShift', 'languageSentiment'],
      colors: Array.isArray(heatmap.colors) ? (heatmap.colors as string[][]) : defaultHeat,
      values: Array.isArray(heatmap.values) ? (heatmap.values as (number | null)[][]) : null,
    },
    behaviorPaths: {
      triggers: Array.isArray(behavior.triggers) ? (behavior.triggers as string[][]) : [],
      states: Array.isArray(behavior.states) ? (behavior.states as string[][]) : [],
      outcomes: Array.isArray(behavior.outcomes) ? (behavior.outcomes as string[][]) : [],
      flows: Array.isArray(behavior.flows)
        ? (behavior.flows as Perception['behaviorPaths']['flows'])
        : [],
      noteTitle: typeof behavior.noteTitle === 'string' ? behavior.noteTitle : 'No behavior paths yet',
      noteBody: typeof behavior.noteBody === 'string' ? behavior.noteBody : '',
    },
    concernDrivers: Array.isArray(r.concernDrivers)
      ? (r.concernDrivers as Perception['concernDrivers']).filter((d) => (d.value ?? 0) > 0)
      : [],
    concernDeltaPct: r.concernDeltaPct != null ? num(r.concernDeltaPct) : null,
    insight: typeof r.insight === 'string' ? r.insight : '',
    insightBanner: typeof r.insightBanner === 'string' ? r.insightBanner : typeof r.insight === 'string' ? r.insight : '',
    calibration: {
      precision: num(calib.precision),
      recall: num(calib.recall),
      concernThreshold: calib.concernThreshold != null ? num(calib.concernThreshold) : 0.72,
      confidenceFloor: calib.confidenceFloor != null ? num(calib.confidenceFloor) : 0.65,
      requireTwoModalities: calib.requireTwoModalities !== false,
      policy: typeof calib.policy === 'string' ? calib.policy : 'Balanced',
      reviewQueueCount: num(calib.reviewQueueCount),
      curve: Array.isArray(calib.curve) ? (calib.curve as { x: number; y: number }[]) : [],
      metricNote: typeof calib.metricNote === 'string' ? calib.metricNote : undefined,
      dualModalitySessions: calib.dualModalitySessions != null ? num(calib.dualModalitySessions) : undefined,
      scoreCount: calib.scoreCount != null ? num(calib.scoreCount) : undefined,
    },
    channels: channels
      ? {
          sessions: num(channels.sessions),
          language: num(channels.language),
          voice: num(channels.voice),
          video: num(channels.video),
          videoExpected: num(channels.videoExpected),
        }
      : null,
    fusion: fusion
      ? Object.fromEntries(Object.entries(fusion).map(([k, v]) => [k, num(v)]))
      : null,
    agents: Array.isArray(r.agents) ? (r.agents as string[]) : [],
  };
}

// Compatibility export: prompt content belongs to the agent configuration domain.
export { AGENT_DEFAULTS_VERSION, DEFAULT_AGENT_PROMPTS, AGENT_PERSONA_PROMPTS, promptsForAgent } from './agent-defaults';

export const api = {
  authConfig: () =>
    req<{ region: string; userPoolId: string | null; userPoolClientId: string | null }>('/auth/config'),
  listSessions: (opts: { limit?: number; offset?: number } = {}) => {
    const limit = opts.limit ?? 50;
    const offset = opts.offset ?? 0;
    const q = new URLSearchParams({
      limit: String(limit),
      offset: String(offset),
    });
    return req<{
      sessions: SessionSummary[];
      total?: number;
      hasMore?: boolean;
      nextOffset?: number | null;
      limit?: number;
      offset?: number;
    }>(`/admin/sessions?${q.toString()}`);
  },
  sessionAgentCounts: (days = 30) =>
    req<{
      days: number;
      since: string;
      counts: Record<string, { total: number; completed: number }>;
    }>(`/admin/sessions/agent-counts?days=${encodeURIComponent(String(days))}`),
  getSession: (id: string) => req<SessionDetail>(`/admin/sessions/${id}`),
  deleteSession: (id: string) => req<{ ok: boolean }>(`/admin/sessions/${id}`, { method: 'DELETE' }),
  removeSessionVideo: (id: string) =>
    req<{ ok: boolean; videoRemoved?: boolean; alreadyRemoved?: boolean; deletedCount?: number }>(
      `/admin/sessions/${encodeURIComponent(id)}/video`,
      { method: 'DELETE' },
    ),
  rerunSessionPerception: (id: string) =>
    req<{ ok: boolean; queued?: boolean; videoPerceptionStatus?: string; error?: string }>(
      `/admin/sessions/${encodeURIComponent(id)}/perception/rerun`,
      { method: 'POST', body: '{}' },
    ),
  flagSession: (id: string) =>
    req<{ ok: boolean; status: string }>(`/admin/sessions/${id}/flag`, { method: 'POST', body: '{}' }),
  overview: async (range: '24h' | '7d' | '30d' = '24h') =>
    normalizeOverview(await req<unknown>(`/admin/overview?range=${encodeURIComponent(range)}`)),
  refreshOverview: (range: '24h' | '7d' | '30d' = '24h') =>
    req<{ ok: boolean; queued: boolean; range: string; message?: string }>(
      '/admin/overview/refresh',
      { method: 'POST', body: JSON.stringify({ range }) },
    ),
  perception: async (range: '24h' | '7d' | '30d' = '30d', opts?: { agentId?: string }) => {
    const q = new URLSearchParams({ range });
    if (opts?.agentId) q.set('agentId', opts.agentId);
    return normalizePerception(await req<unknown>(`/admin/perception?${q.toString()}`));
  },
  listAgents: () => req<{ agents: Agent[] }>('/admin/agents'),
  getAgent: (agentId: string) =>
    req<Agent>(`/admin/agents/${encodeURIComponent(agentId)}`),
  upsertAgent: (agent: Partial<Agent> & { agentId: string }) =>
    req<Agent>('/admin/agents', { method: 'POST', body: JSON.stringify(agent) }),
  updateAgent: (agentId: string, body: Partial<Agent>) =>
    req<Agent>(`/admin/agents/${encodeURIComponent(agentId)}`, { method: 'PUT', body: JSON.stringify(body) }),
  publishAgent: (agentId: string) =>
    req<Agent>(`/admin/agents/${encodeURIComponent(agentId)}/publish`, { method: 'POST', body: '{}' }),
  uploadAgentAvatar: (agentId: string, dataBase64: string) =>
    req<Agent>(`/admin/agents/${encodeURIComponent(agentId)}/avatar`, {
      method: 'POST',
      body: JSON.stringify({ contentType: 'image/jpeg', dataBase64 }),
    }),
  listAgentVersions: (agentId: string, opts?: { full?: boolean }) => {
    const q = opts?.full ? '?full=1' : '';
    return req<{ agentId: string; versions: AgentVersion[] }>(
      `/admin/agents/${encodeURIComponent(agentId)}/versions${q}`,
    );
  },
  getAgentVersion: (agentId: string, version: number) =>
    req<AgentVersion>(`/admin/agents/${encodeURIComponent(agentId)}/versions/${version}`),
  deleteAgent: (agentId: string) =>
    req<{ ok: boolean }>(`/admin/agents/${encodeURIComponent(agentId)}`, { method: 'DELETE' }),
  getSettings: () => req<Settings>('/admin/settings'),
  putSettings: (body: Settings) =>
    req<Settings>('/admin/settings', { method: 'PUT', body: JSON.stringify(body) }),
  listCartesiaVoices: async (opts?: {
    q?: string;
    gender?: string;
    language?: string;
    limit?: number;
    startingAfter?: string;
  }) => {
    const q = new URLSearchParams();
    if (opts?.q) q.set('q', opts.q);
    if (opts?.gender) q.set('gender', opts.gender);
    if (opts?.language) q.set('language', opts.language);
    if (opts?.limit != null) q.set('limit', String(opts.limit));
    if (opts?.startingAfter) q.set('starting_after', opts.startingAfter);
    const qs = q.toString();
    const raw = await req<{
      voices?: CartesiaVoice[];
      data?: CartesiaVoice[];
      hasMore?: boolean;
      has_more?: boolean;
      nextPage?: string | null;
      next_page?: string | null;
    }>(`/admin/cartesia/voices${qs ? `?${qs}` : ''}`);
    const voices = Array.isArray(raw.voices)
      ? raw.voices
      : Array.isArray(raw.data)
        ? raw.data
        : [];
    return {
      voices,
      hasMore: Boolean(raw.hasMore ?? raw.has_more),
      nextPage: raw.nextPage ?? raw.next_page ?? null,
    };
  },
  getCartesiaVoicePreview: (voiceId: string) =>
    req<{ contentType: string; data: string; voiceId: string; name?: string | null }>(
      `/admin/cartesia/voices/${encodeURIComponent(voiceId)}/preview`,
    ),
  listElevenLabsVoices: async () => {
    const raw = await req<{
      voices?: Array<{ voiceId: string; name: string; previewUrl?: string | null }>;
    }>('/admin/elevenlabs/voices');
    return { voices: Array.isArray(raw.voices) ? raw.voices : [] };
  },
  getElevenLabsVoicePreview: (voiceId: string) =>
    req<{
      contentType: string;
      data: string;
      voiceId: string;
      name?: string | null;
      source?: string;
    }>(`/admin/elevenlabs/voices/${encodeURIComponent(voiceId)}/preview`),
  synthesizeCartesiaSpeech: (body: {
    transcript: string;
    voiceId?: string | null;
    speed?: number | null;
    volume?: number | null;
    emotion?: string | null;
  }) =>
    req<{ contentType: string; data: string; voiceId: string; transcript: string }>(
      '/admin/cartesia/tts',
      { method: 'POST', body: JSON.stringify(body) },
    ),
  listLiveAvatars: (opts?: { page?: number; pageSize?: number }) => {
    const q = new URLSearchParams();
    if (opts?.page != null) q.set('page', String(opts.page));
    if (opts?.pageSize != null) q.set('page_size', String(opts.pageSize));
    const qs = q.toString();
    return req<{
      count: number;
      customCount?: number;
      presetCount?: number;
      avatars: LiveAvatarRow[];
      partialErrors?: { custom?: string | null; preset?: string | null };
    }>(`/admin/liveavatar/avatars${qs ? `?${qs}` : ''}`);
  },
  getLiveAvatar: (avatarId: string) =>
    req<{ avatar: LiveAvatarRow }>(
      `/admin/liveavatar/avatars/${encodeURIComponent(avatarId)}`,
    ),
  bindAgentLiveAvatar: (
    agentId: string,
    body: {
      liveAvatarId?: string | null;
      videoAvatarEnabled?: boolean;
      liveAvatarSandbox?: boolean;
    },
  ) =>
    req<Agent>(`/admin/agents/${encodeURIComponent(agentId)}/live-avatar/bind`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  getCredentials: () =>
    req<{ slots: CredentialSlot[]; modelCatalog?: Settings['modelCatalog'] }>(
      '/admin/credentials',
    ),
  putCredentials: (body: {
    slots?: Array<Partial<CredentialSlot> & { id?: string; secret?: string }>;
    deleteIds?: string[];
  }) => req<{ slots: CredentialSlot[] }>('/admin/credentials', { method: 'PUT', body: JSON.stringify(body) }),
  testCredential: (slotId: string) =>
    req<{
      ok: boolean;
      slotId: string;
      provider: string;
      latencyMs?: number;
      modelId?: string;
      sample?: string;
      error?: string;
    }>('/admin/credentials/test', { method: 'POST', body: JSON.stringify({ slotId }) }),
  probeProviders: (body: {
    modality?: string;
    entries: Array<{ slotId: string; modelId: string }>;
    runs?: number;
  }) =>
    req<{
      results: ProbeResult[];
      suggestedOrder: LlmRoutingEntry[];
      measuredAt: string;
    }>('/admin/providers/probe', { method: 'POST', body: JSON.stringify(body) }),
  listCheckups: (protocolCount?: number) =>
    req<CheckupsResponse>(
      `/admin/checkups${protocolCount != null ? `?protocolCount=${protocolCount}` : ''}`,
    ),
  getCheckup: (protocolId: string) =>
    req<CheckupDetailResponse>(`/admin/checkups/${encodeURIComponent(protocolId)}`),
  saveCheckupResult: (
    protocolId: string,
    body: { status: 'pass' | 'fail'; sessionId?: string; notes: string },
  ) =>
    req<{ result: CheckupResult }>(`/admin/checkups/${encodeURIComponent(protocolId)}/results`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  deleteCheckupResult: (protocolId: string, resultId: string) =>
    req<{ ok: boolean }>(
      `/admin/checkups/${encodeURIComponent(protocolId)}/results/${encodeURIComponent(resultId)}`,
      { method: 'DELETE' },
    ),

  me: {
    get: (opts?: { login?: boolean }) => {
      const q = opts?.login ? '?login=1' : '';
      return req<AdminMe>(`/admin/me${q}`);
    },
    settings: () => req<UserSettings>('/admin/me/settings'),
    updateSettings: (body: Partial<UserSettings>) =>
      req<UserSettings>('/admin/me/settings', {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
  },

  listAuditEvents: (opts?: {
    from?: string;
    to?: string;
    category?: string;
    risk?: string;
    actor?: string;
    q?: string;
    outcome?: string;
    limit?: number;
    offset?: number;
  }) => {
    const q = new URLSearchParams();
    if (opts?.from) q.set('from', opts.from);
    if (opts?.to) q.set('to', opts.to);
    if (opts?.category) q.set('category', opts.category);
    if (opts?.risk) q.set('risk', opts.risk);
    if (opts?.actor) q.set('actor', opts.actor);
    if (opts?.q) q.set('q', opts.q);
    if (opts?.outcome) q.set('outcome', opts.outcome);
    if (opts?.limit != null) q.set('limit', String(opts.limit));
    if (opts?.offset != null) q.set('offset', String(opts.offset));
    const qs = q.toString();
    return req<AuditListResponse>(`/admin/audit${qs ? `?${qs}` : ''}`);
  },
  getAuditEvent: (eventId: string) =>
    req<{ event: AuditEventDetail }>(`/admin/audit/${encodeURIComponent(eventId)}`),
  exportAuditEvents: (opts?: {
    from?: string;
    to?: string;
    category?: string;
    risk?: string;
    actor?: string;
    q?: string;
    limit?: number;
  }) => {
    const q = new URLSearchParams();
    if (opts?.from) q.set('from', opts.from);
    if (opts?.to) q.set('to', opts.to);
    if (opts?.category) q.set('category', opts.category);
    if (opts?.risk) q.set('risk', opts.risk);
    if (opts?.actor) q.set('actor', opts.actor);
    if (opts?.q) q.set('q', opts.q);
    if (opts?.limit != null) q.set('limit', String(opts.limit));
    const qs = q.toString();
    return req<AuditExportPack>(`/admin/audit/export${qs ? `?${qs}` : ''}`);
  },

  users: {
    list: (opts?: {
      q?: string;
      status?: string;
      roleId?: string;
      limit?: number;
      offset?: number;
    }) => {
      const q = new URLSearchParams();
      if (opts?.q) q.set('q', opts.q);
      if (opts?.status) q.set('status', opts.status);
      if (opts?.roleId) q.set('roleId', opts.roleId);
      if (opts?.limit != null) q.set('limit', String(opts.limit));
      if (opts?.offset != null) q.set('offset', String(opts.offset));
      const qs = q.toString();
      return req<UsersListResponse>(`/admin/users${qs ? `?${qs}` : ''}`);
    },
    create: (body: {
      firstName: string;
      lastName: string;
      email: string;
      roleId: string;
    }) =>
      req<{ user: PlatformUser; correlationId?: string }>('/admin/users', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    update: (
      userId: string,
      body: Partial<{
        firstName: string;
        lastName: string;
        roleId: string;
        version: number;
      }>,
    ) =>
      req<{ user: PlatformUser }>(`/admin/users/${encodeURIComponent(userId)}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    remove: (userId: string) =>
      req<{ ok: boolean }>(`/admin/users/${encodeURIComponent(userId)}`, {
        method: 'DELETE',
      }),
    resendInvitation: (userId: string) =>
      req<{ ok: boolean; correlationId?: string }>(
        `/admin/users/${encodeURIComponent(userId)}/resend-invitation`,
        { method: 'POST' },
      ),
    revokeInvitation: (userId: string) =>
      req<{ ok: boolean }>(
        `/admin/users/${encodeURIComponent(userId)}/revoke-invitation`,
        { method: 'POST' },
      ),
    suspend: (userId: string) =>
      req<{ ok: boolean }>(`/admin/users/${encodeURIComponent(userId)}/suspend`, {
        method: 'POST',
      }),
    reactivate: (userId: string) =>
      req<{ ok: boolean }>(
        `/admin/users/${encodeURIComponent(userId)}/reactivate`,
        { method: 'POST' },
      ),
    passwordReset: (userId: string) =>
      req<{ ok: boolean; correlationId?: string; message?: string }>(
        `/admin/users/${encodeURIComponent(userId)}/password-reset`,
        { method: 'POST' },
      ),
  },

  roles: {
    list: (opts?: {
      q?: string;
      type?: string;
      limit?: number;
      offset?: number;
    }) => {
      const q = new URLSearchParams();
      if (opts?.q) q.set('q', opts.q);
      if (opts?.type) q.set('type', opts.type);
      if (opts?.limit != null) q.set('limit', String(opts.limit));
      if (opts?.offset != null) q.set('offset', String(opts.offset));
      const qs = q.toString();
      return req<RolesListResponse>(`/admin/roles${qs ? `?${qs}` : ''}`);
    },
    get: (roleId: string) =>
      req<{ role: PlatformRole }>(`/admin/roles/${encodeURIComponent(roleId)}`),
    create: (body: {
      name: string;
      description?: string;
      templateRoleId?: string | null;
      allowedAgentIds?: string[];
    }) =>
      req<{ role: PlatformRole }>('/admin/roles', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    update: (
      roleId: string,
      body: {
        name?: string;
        description?: string;
        permissions?: string[];
        allowedAgentIds?: string[];
        version?: number;
      },
    ) =>
      req<{ role: PlatformRole }>(`/admin/roles/${encodeURIComponent(roleId)}`, {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    remove: (roleId: string) =>
      req<{ ok: boolean }>(`/admin/roles/${encodeURIComponent(roleId)}`, {
        method: 'DELETE',
      }),
    permissions: () =>
      req<{ groups: PermissionGroup[] }>('/admin/permissions'),
  },

  auth: {
    acceptInvitation: (body: { token: string; password: string }) =>
      req<{ ok: boolean; email?: string; correlationId?: string }>(
        '/auth/invitations/accept',
        { method: 'POST', body: JSON.stringify(body) },
      ),
    requestPasswordReset: (email: string) =>
      req<{ ok: boolean; message?: string; correlationId?: string }>(
        '/auth/password-reset/request',
        { method: 'POST', body: JSON.stringify({ email }) },
      ),
    confirmPasswordReset: (body: { token: string; password: string }) =>
      req<{ ok: boolean; correlationId?: string }>(
        '/auth/password-reset/confirm',
        { method: 'POST', body: JSON.stringify(body) },
      ),
  },
};

/** Live-first helper: returns live data or falls back to mock on error/empty. */
export async function liveOrMock<T>(live: () => Promise<T>, mock: () => T): Promise<{ data: T; source: 'live' | 'mock' }> {
  try {
    const data = await live();
    return { data, source: 'live' };
  } catch {
    return { data: mock(), source: 'mock' };
  }
}
